import React from 'react';

import { useContextRequired } from '@nfctron/nexus-react';

import { Component, ComponentDisplayName } from '@root/lib/types/components';

import { type MultiViewContextProps, type MultiViewProps, type MultiViewViewProps } from './MultiView.types';

/**
 * MultiView component context
 * @type {React.Context<MultiViewContextProps<any> | undefined>}
 */
const MultiViewContext = React.createContext<MultiViewContextProps<any> | undefined>(undefined);

/**
 * MultiView component
 * @author filipditrich <ditrich@nfctron.com>
 * @constructor
 */
export function MultiView<V extends string | number>({ provider, children, ...props }: MultiViewProps<V>) {
	return <MultiViewContext value={provider}>{children}</MultiViewContext>;
}

/**
 * MultiView component display name
 * @type {ComponentDisplayName}
 */
MultiView.displayName = 'component/MultiView';

/**
 * MultiView/View component
 * @returns {JSX.Element | null}
 * @constructor
 */
function MultiViewView<V extends string | number>({ children, view, ...props }: MultiViewViewProps<V>) {
	const { activeView } = useContextRequired(MultiViewContext);
	return (Array.isArray(view) ? view : [view]).includes(activeView) ? <React.Fragment>{children}</React.Fragment> : null;
}

/**
 * MultiView/View component display name
 * @type {ComponentDisplayName}
 */
MultiViewView.displayName = 'component/MultiView/View';

/**
 * Assign MultiView/View component to MultiView component
 * @type {Component<MultiViewViewProps>}
 */
MultiView.View = MultiViewView;
